import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Eye,
  FileText,
  Sparkles
} from "lucide-react";

export default function AdminSiteConfig() {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    site_name: "",
    site_tagline: "",
    hero_title: "",
    hero_subtitle: "",
    hero_cta_text: "",
    pricing_title: "",
    pricing_subtitle: "",
    cta_title: "",
    cta_subtitle: "",
    cta_button_text: "",
    footer_text: ""
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-config`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config", error);
      toast.error("Failed to load site configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-config`, config, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Site configuration saved successfully!");
    } catch (error) {
      console.error("Failed to save config", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset to default configuration? This cannot be undone.")) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/site-config/reset`, {}, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setConfig(response.data);
      toast.success("Configuration reset to defaults");
    } catch (error) {
      console.error("Failed to reset config", error);
      toast.error("Failed to reset configuration");
    } finally {
      setLoading(false);
    }
  };

  const openPreview = () => {
    window.open("/", "_blank");
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="font-serif text-2xl font-bold">Site Configuration</h1>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-site-config">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-gold" />
          <div>
            <h1 className="font-serif text-2xl font-bold">Site Configuration</h1>
            <p className="text-sm text-muted-foreground">Manage your website's text and marketing copy</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPreview} data-testid="preview-btn">
            <Eye className="h-4 w-4 mr-2" />
            Preview Site
          </Button>
          <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Basic Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif flex items-center gap-2">
            <FileText className="h-5 w-5 text-gold" />
            Basic Information
          </CardTitle>
          <CardDescription>Site name and tagline shown in the header</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="site_name">Site Name</Label>
              <Input
                id="site_name"
                data-testid="input-site-name"
                value={config.site_name || ""}
                onChange={(e) => handleInputChange("site_name", e.target.value)}
                placeholder="The Juris Archive"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site_tagline">Site Tagline</Label>
              <Input
                id="site_tagline"
                data-testid="input-site-tagline"
                value={config.site_tagline || ""}
                onChange={(e) => handleInputChange("site_tagline", e.target.value)}
                placeholder="Law School, Remixed"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hero Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-gold" />
            Hero Section
          </CardTitle>
          <CardDescription>Main headline and call-to-action on the landing page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hero_title">Hero Title</Label>
            <Textarea
              id="hero_title"
              data-testid="input-hero-title"
              value={config.hero_title || ""}
              onChange={(e) => handleInputChange("hero_title", e.target.value)}
              placeholder="Master Law School\nYour Way"
              rows={2}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">Use \n for line breaks</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
            <Textarea
              id="hero_subtitle"
              data-testid="input-hero-subtitle"
              value={config.hero_subtitle || ""}
              onChange={(e) => handleInputChange("hero_subtitle", e.target.value)}
              placeholder="Stream audio lessons and visual flowcharts..."
              rows={3}
              className="resize-none"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hero_cta_text">Hero Button Text</Label>
            <Input
              id="hero_cta_text"
              data-testid="input-hero-cta"
              value={config.hero_cta_text || ""}
              onChange={(e) => handleInputChange("hero_cta_text", e.target.value)}
              placeholder="Subscribe Now"
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif">Pricing Section</CardTitle>
          <CardDescription>Headlines for the pricing/subscribe page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pricing_title">Pricing Title</Label>
              <Input
                id="pricing_title"
                data-testid="input-pricing-title"
                value={config.pricing_title || ""}
                onChange={(e) => handleInputChange("pricing_title", e.target.value)}
                placeholder="Choose Your Plan"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pricing_subtitle">Pricing Subtitle</Label>
              <Input
                id="pricing_subtitle"
                data-testid="input-pricing-subtitle"
                value={config.pricing_subtitle || ""}
                onChange={(e) => handleInputChange("pricing_subtitle", e.target.value)}
                placeholder="Subscribe today and start studying smarter..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif">Call to Action Section</CardTitle>
          <CardDescription>Bottom CTA section on the landing page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cta_title">CTA Title</Label>
            <Input
              id="cta_title"
              data-testid="input-cta-title"
              value={config.cta_title || ""}
              onChange={(e) => handleInputChange("cta_title", e.target.value)}
              placeholder="Ready to Ace Law School?"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cta_subtitle">CTA Subtitle</Label>
            <Textarea
              id="cta_subtitle"
              data-testid="input-cta-subtitle"
              value={config.cta_subtitle || ""}
              onChange={(e) => handleInputChange("cta_subtitle", e.target.value)}
              placeholder="Join thousands of law students..."
              rows={2}
              className="resize-none"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cta_button_text">CTA Button Text</Label>
            <Input
              id="cta_button_text"
              data-testid="input-cta-button"
              value={config.cta_button_text || ""}
              onChange={(e) => handleInputChange("cta_button_text", e.target.value)}
              placeholder="Subscribe Now"
            />
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-serif">Footer</CardTitle>
          <CardDescription>Copyright and footer text</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="footer_text">Footer Text</Label>
            <Input
              id="footer_text"
              data-testid="input-footer-text"
              value={config.footer_text || ""}
              onChange={(e) => handleInputChange("footer_text", e.target.value)}
              placeholder="© 2025 The Juris Archive..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button (Mobile) */}
      <div className="flex justify-end gap-2 sm:hidden">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
